import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import '../../../../AgGridStyle.css';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Box, Typography, CircularProgress, Tooltip, IconButton, Button } from '@mui/material';
import { usePermissions } from '../../../../GlobalFunction/userPermissions';
import { useSelector } from 'react-redux';
import { selectGlobalProperty } from '../../../../redux/propertySlice';
import { useNavigate, useLocation } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import HomeIcon from '@mui/icons-material/Home';
import GenarateBeds from "./GenerateBeds";
import BedUpdation from "./BedUpdation";
import { getFormMode } from "../../../../lib/helpers";
import axiosInstance from '../../../../lib/axios';
import * as XLSX from "xlsx";
import SaveAltIcon from '@mui/icons-material/SaveAlt';

ModuleRegistry.registerModules([AllCommunityModule]);

const columns = [
  { id: "RowNum", label: "Sr.No.", minWidth: 3 },
  { id: "PropName", label: "Property", minWidth: 100 },
  { id: "WingName", label: "Wing", minWidth: 40 },
  { id: "FloorName", label: "Floor", minWidth: 60 },
  { id: "RoomTypeName", label: "RoomType", minWidth: 60 },
  { id: "RoomNo", label: "RoomNo", minWidth: 10 },
  { id: "TotalBeds", label: "TotalBeds", minWidth: 40 },
  { id: "RoombedName", label: "BedNo", minWidth: 40 },
  { id: "RoomBedTypeName", label: "BedType", minWidth: 40 },
  { id: "BedRent", label: "BedRent", minWidth: 80 },
  { id: "Remark", label: "Remark", minWidth: 100 },
  { id: "Status", label: "Status", minWidth: 20 },
];

const RoomBedNumberTable = () => {
  const [rowData, setRowData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const gridRef = useRef();
  const navigate = useNavigate();
  const location = useLocation();
  const userId = sessionStorage.getItem('UserId');
  const permissions = usePermissions("Bed Master");
  const { AddRec = "0" } = permissions || {};
  const globalProperty = useSelector(selectGlobalProperty);
  const [isExporting, setIsExporting] = useState(false);

  const Form_Mode = getFormMode();
  const [mode] = useState(() =>
    location?.state?.RoomId ? Form_Mode.read : Form_Mode.add
  );

  const colDefs = useMemo(() => [
    { field: 'RowNum', headerName: 'Sr.No', width: 70 },
    { field: 'PropName', headerName: 'Property', width: 200 },
    { field: 'WingName', headerName: 'Wing', width: 100 },
    { field: 'FloorName', headerName: 'Floor', width: 100 },
    { field: 'RoomTypeName', headerName: 'Type', width: 150 },
    { field: 'RoomNo', headerName: 'Room No', width: 120 },
    { field: 'TotalBeds', headerName: 'Total Beds', width: 100 },
    { field: 'RoombedName', headerName: 'Bed No', width: 130 },
    { field: 'RoomBedTypeName', headerName: 'Bed Type', width: 130 },
    { field: 'BedRent', headerName: 'BedRent', width: 100 },
    { field: 'Remark', headerName: 'Remark', width: 100 },
    {
      field: 'Status',
      headerName: 'Status',
      width: 120,
      cellStyle: (params) => ({
        color: params.value === 'Active' ? 'green' : 'red',
        fontWeight: 'bold',
      }),
    },
  ], []);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    floatingFilter: true,
    tooltipValueGetter: (params) => params.value ?? '',
  }), []);

  const fetchRoomBedData = useCallback(async () => {
    if (!globalProperty) {
      setRowData([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const response = await axiosInstance.post("RoomBedMst/getAllRoomBedMstDashBoard?currentPage=1&limit=10", {
        start: 1,
        PageSize: 1,
        SearchText: "",
        PropId: globalProperty
      });

      if (response?.data?.Status === 0 && response.data.Data) {
        const formattedData = response.data.Data.map((room, index) => ({
          ...room,
          Status: room.Status == 1 ? 'Active' : 'Inactive',
        }));
        setRowData(formattedData);
      } else {
        setRowData([]);
        toast.info(response?.data?.message || 'No records found');
      }
    } catch (error) {
      toast.error('Failed to load room data');
      setRowData([]);
    } finally {
      setIsLoading(false);
    }
  }, [globalProperty]);

  useEffect(() => {
    fetchRoomBedData();
  }, [fetchRoomBedData]);

  const handleExportToExcel = async () => {
    setIsExporting(true);
    try {
      const exportResponse = await axiosInstance.post("RoomBedMst/getAllRoomBedMstDashBoard?currentPage=1&limit=10000", {
        start: 1,
        PageSize: 1,
        SearchText: "",
        PropId: globalProperty
      });

      if (exportResponse instanceof Error) {
        throw exportResponse;
      }

      const { data: { Status, Data, responseStatusCode } } = exportResponse;

      if (Status === 0 && responseStatusCode === 1 && Array.isArray(Data)) {
        const worksheetData = Data.map((row) => {
          let rowData = {};
          columns.forEach((col) => {
            rowData[col.label] = row[col.id] || "";
          });
          return rowData;
        });

        const worksheet = XLSX.utils.json_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        const worksheetName = "BedMasterData";
        const currentDate = new Date().toISOString().split('T')[0];
        const fileName = `BedMasterData_${currentDate}.xlsx`;

        XLSX.utils.book_append_sheet(workbook, worksheet, worksheetName);
        await new Promise((resolve) => setTimeout(resolve, 1000));
        XLSX.writeFile(workbook, fileName);

        toast.success("Data exported successfully!");
      }
    } catch (error) {
      toast.error("Failed to export data");
    } finally {
      setIsExporting(false);
    }
  };

  const handleRowDoubleClick = useCallback((params) => {
    const roomId = params?.data?.RoomBedId;
    if (roomId) {
      navigate('/roomBedNumber', { state: { RoomBedId: roomId, mode: 'view' } });
    }
  }, [navigate]);

  const handlePermittedClick = () => {
    if (AddRec === "1") {
      navigate('/roomBedNumber');
    }
  };

  const handleClearFilters = () => {
    gridRef.current?.api?.setFilterModel(null);
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '90vh',
        padding: '10px',
        backgroundColor: 'rgb(249, 249, 249)',
        position: 'relative'
      }}
    >
      <ToastContainer />
      <Box
        sx={{
          backgroundColor: "#ffffff",
          padding: { xs: "8px 16px", sm: "12px 16px" },
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
          borderRadius: "12px",
          margin: "10px auto",
          maxWidth: "95%",
          flexDirection: { xs: "column", sm: "column", md: "row" },
          gap: 2,
        }}
      >
        <Breadcrumbs aria-label="breadcrumb" separator="›">
          <Link
            underline="hover"
            color="#635bff"
            sx={{ cursor: "pointer", fontWeight: "500", display: "flex", alignItems: "center" }}
            onClick={() => navigate('/dashboard')}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </Link>
          <Link
            underline="hover"
            color="#635bff"
            sx={{ cursor: "pointer", fontWeight: "500" }}
            onClick={() => navigate('/masters/property')}
          >
            Property
          </Link>
          <Typography color="text.primary" sx={{ fontWeight: "500" }}>
            Bed Master
          </Typography>
        </Breadcrumbs>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: { xs: 1, sm: 1, md: 0 }, flexShrink: 0 }}>
          <Tooltip title='Clear all applied filter' arrow>
            <Button
              variant="outlined"
              onClick={handleClearFilters}
              size="small"
              sx={{
                borderRadius: "50px",
                textTransform: "none",
                borderColor: "#635bff",
                color: "#635bff",
                "&:hover": {
                  borderColor: "#524ccc",
                  backgroundColor: "rgba(99, 91, 255, 0.04)",
                }
              }}
            >
              Clear Filters
            </Button>
          </Tooltip>

          <GenarateBeds
            PropId={globalProperty}
            UserId={userId}
            fetchRoomBedData={fetchRoomBedData}
          />

          <BedUpdation
            PropId={globalProperty}
            rowData={rowData}
            fetchRoomBedData={fetchRoomBedData}
          />

          {/* Add New Button */}
          <Tooltip
            title={AddRec !== "1" ? "You have no permission to add record" : "Add New Record"}
            placement="top"
            arrow
          >
            <span>
              <Button
                variant="contained"
                onClick={handlePermittedClick}
                size="small"
                disabled={AddRec !== "1"}
                sx={{
                  background: AddRec === "1" ? "#635bff" : "rgba(0, 0, 0, 0.12)",
                  color: AddRec === "1" ? "white" : "rgba(0, 0, 0, 0.26)",
                  borderRadius: "50px",
                  padding: "6px 15px",
                  boxShadow: AddRec === "1" ? "0px 6px 15px rgba(0, 0, 0, 0.2)" : "none",
                  "&:hover": {
                    background: AddRec === "1" ? "#524ccc" : "rgba(0, 0, 0, 0.12)"
                  }
                }}
              >
                <AddIcon fontSize="small" sx={{ mr: 0.5 }} /> New
              </Button>
            </span>
          </Tooltip>

          <Tooltip title="Export to excel" arrow>
            <span>
              <Button
                variant="contained"
                onClick={handleExportToExcel}
                size="small"
                sx={{
                  background: "#635bff",
                  color: "white",
                  borderRadius: "50px",
                  padding: "6px 15px",
                  boxShadow: "0px 6px 15px rgba(0, 0, 0, 0.2)",
                  "&:hover": {
                    background: "#524ccc"
                  }
                }}
                disabled={isExporting || rowData.length === 0}
              >
                <SaveAltIcon sx={{ mr: 1, fontSize: '18px' }} />
                {isExporting ? "Downloading" : "Export"}
              </Button>
            </span>
          </Tooltip>

          {/* Back Button */}
          <Tooltip title='Previous Page' arrow>
            <IconButton
              onClick={() => navigate('/masters/property')}
              sx={{
                backgroundColor: '#635bff',
                borderRadius: '50%',
                width: 35,
                height: 35,
                '&:hover': { backgroundColor: '#635bff' },
                boxShadow: 3,
              }}
            >
              <ArrowBackIcon sx={{ color: 'white' }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box className="table-grid ag-theme-alpine"
        sx={{ height: 'calc(97% - 56px)', width: '95%', margin: '0 auto', position: 'relative' }}
      >
        {isLoading ? (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 10,
              textAlign: 'center',
            }}
          >
            <CircularProgress size="3rem" sx={{ color: '#635bff' }} />
            <Typography sx={{ marginTop: 2, color: '#635bff', fontWeight: 'bold' }}>
              Loading data...
            </Typography>
          </Box>
        ) : (
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            columnDefs={colDefs}
            defaultColDef={defaultColDef}
            pagination={true}
            paginationPageSize={1000}
            onRowDoubleClicked={handleRowDoubleClick}
            paginationPageSizeSelector={[100, 500, 1000, 5000]}
            enableCellTextSelection={true}
            rowHeight={25}
            headerHeight={30}
            floatingFiltersHeight={28}
            overlayNoRowsTemplate={'<span class="ag-overlay-loading-center">No rooms to show</span>'}
            tooltipShowDelay={0}
            getRowStyle={(params) => ({
              backgroundColor: params.node.rowIndex % 2 === 0 ? '#f1f5fd' : '#ffffff'
            })}
          />
        )}
      </Box>

      <Box sx={{
        position: 'absolute',
        bottom: 16,
        left: 70,
        fontSize: '14px',
        color: 'black',
      }}>
        Total Records: <b>{rowData.length}</b>
      </Box>
    </Box>
  );
};

export default RoomBedNumberTable;